import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserService } from '../../core/users/services/user.service';
import { MediaHavenPlugin } from '../entities/mediahaven-plugins.entity';
import { ShModulesService } from '../services/modules.service';

@Injectable({
    providedIn: 'root'
})
export abstract class PluginGuard {
    protected redirectUrl = '/';

    protected abstract PLUGINS_TO_CHECK: MediaHavenPlugin[];

    protected modulesService: ShModulesService = inject(ShModulesService);
    protected userService: UserService = inject(UserService);
    protected router: Router = inject(Router);

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<true | UrlTree> {
        return this.checkPlugins(this.PLUGINS_TO_CHECK).pipe(
            map(isAllowed => {
                if (!isAllowed) {
                    if (this.PLUGINS_TO_CHECK.length === 1) {
                        console.error(
                            `Required plugin ${this.PLUGINS_TO_CHECK[0]} is not active for the current organisation.`
                        );
                    } else {
                        console.error(
                            `None of the required plugins ${this.PLUGINS_TO_CHECK.join(', ')} are active for the current organisation.`
                        );
                    }

                    return this.router.createUrlTree([this.redirectUrl]);
                }

                return true;
            })
        );
    }

    checkPlugins(pluginsToCheck: MediaHavenPlugin[]): Observable<boolean> {
        return this.userService.getCurrentUser().pipe(
            switchMap(user =>
                combineLatest(
                    pluginsToCheck.map(pluginToCheck =>
                        this.modulesService.isActivePlugin(pluginToCheck, user.Organisation.Id)
                    )
                )
            ),
            map(pluginsEnabled => pluginsEnabled.some(pluginEnabled => pluginEnabled))
        );
    }

    public hasAccess(): Observable<boolean> {
        return this.checkPlugins(this.PLUGINS_TO_CHECK);
    }
}
